import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'aria-describedby' | 'id'
> {
  readonly description?: string;
  readonly describedBy?: string;
  readonly error?: string;
  readonly id?: string;
  readonly label: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { className, describedBy, description, error, id: explicitId, label, required, ...inputProps },
  ref,
) {
  const generatedId = useId();
  const id = explicitId ?? `field-${generatedId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const ariaDescribedBy =
    [describedBy, descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="form-field">
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {description ? (
        <p className="field-description" id={descriptionId}>
          {description}
        </p>
      ) : null}
      <input
        {...inputProps}
        ref={ref}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        className={['text-input', 'touch-target', className].filter(Boolean).join(' ')}
        id={id}
        required={required}
      />
      {error ? (
        <p className="field-error" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
});

export function FormErrorSummary({
  errors,
  title = 'Zkontroluj formulář',
}: {
  readonly errors: readonly string[];
  readonly title?: string;
}) {
  const summary = useRef<HTMLDivElement>(null);
  const errorKey = errors.join('\u0000');
  useEffect(() => {
    if (errorKey) {
      summary.current?.focus();
    }
  }, [errorKey]);

  if (errors.length === 0) {
    return null;
  }
  return (
    <div ref={summary} className="error-summary" role="alert" tabIndex={-1}>
      <strong>{title}</strong>
      <ul>
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  );
}

export function SaveStatus({ children }: { readonly children: ReactNode }) {
  return (
    <p className="save-status" role="status" aria-live="polite" aria-atomic="true">
      {children}
    </p>
  );
}
